// Chat bubble renderers for the "Ask Vidhya" copilot drawer.
// Extracted from the retired full-page ChatPanel — these draw each user/AI
// message and the inline tool-result cards inside the drawer stream.
import React from "react";
import { I } from "../data";
import UpgradeButton from "./UpgradeButton";
import { VidhyaMark } from "./VidhyaMark";
import { StepReveal, chatStepsForMessage } from "./StepReveal";

// ---------- text helpers ----------
// Strip wrapping brackets / quotes the AI sometimes puts around a phrase
// (e.g.  `[Email Prof. Datta]`  or  `"Draft your SOP"` → unwrapped string).
function unwrapWrappers(s) {
  if (!s) return s;
  let t = String(s).trim();
  for (let i = 0; i < 3; i++) {
    const before = t;
    const m =
      t.match(/^\[(.+)\]$/s) ||
      t.match(/^\((.+)\)$/s) ||
      t.match(/^"(.+)"$/s) ||
      t.match(/^'(.+)'$/s) ||
      t.match(/^`(.+)`$/s) ||
      t.match(/^“(.+)”$/s) ||
      t.match(/^‘(.+)’$/s);
    if (m) t = m[1].trim();
    if (t === before) break;
  }
  return t;
}

// Trim any line that is JUST a quoted/bracketed phrase, leaving inline quotes alone.
function cleanAiLine(line) {
  if (!line) return line;
  const stripped = line.trim();
  const unwrapped = unwrapWrappers(stripped);
  if (unwrapped !== stripped) {
    const prefixMatch = line.match(/^(\s*(?:[-*•]\s+|\d+[.)]\s+))/);
    return (prefixMatch ? prefixMatch[1] : "") + unwrapped;
  }
  return line;
}

// Strip ANY tool plumbing so it never reaches the DOM.
function renderRichText(text) {
  const lines = String(text || "").split("\n").map(cleanAiLine);
  const out = [];
  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (/^([-*_])\1{2,}$/.test(trimmed)) return;
    const h = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const txt = h[2].replace(/\*\*/g, "").replace(/[*_#]+$/g, "").trim();
      if (txt) out.push(<h4 key={i} className="ai-memo-h">{txt}</h4>);
      return;
    }
    if (trimmed === "") {
      return;
    }
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ") || trimmed.startsWith("• ")) {
      out.push(
        <li key={i} style={{ marginLeft: 18 }}>
          {trimmed.replace(/^[-*•]\s+/, "").split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
            p.startsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>
          )}
        </li>
      );
      return;
    }
    out.push(
      <p key={i}>
        {line.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
          p.startsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>
        )}
      </p>
    );
  });
  return out;
}

// ---------- Tool card ----------
const TOOL_META = {
  web_browse:          { icon: <I.Globe size={13} />,        label: "Official page",       running: "Researching live sources…" },
  extract:             { icon: <I.Doc size={13} />,          label: "Official page",       running: "Reading the official page…" },
  research_university: { icon: <I.Globe size={13} />,        label: "Program research",    running: "Researching the program…" },
  web_search:          { icon: <I.Search size={13} />,       label: "Live research",       running: "Searching live…" },
  find_professors:     { icon: <I.GraduationCap size={13} />,label: "Faculty",             running: "Looking up faculty…" },
  find_scholarships:   { icon: <I.Trophy size={13} />,       label: "Scholarships",        running: "Finding scholarships…" },
  add_to_shortlist:    { icon: <I.Plus size={13} />,         label: "Added to your board", running: "Adding to your board…" },
  create_task:         { icon: <I.Check size={13} />,        label: "Suggested task",      running: "Thinking of a next step…" },
  draft_email:         { icon: <I.Mail size={13} />,         label: "Email draft",         running: "Drafting your email…" },
};

export function ToolCard({ tool, status, children }) {
  const meta = TOOL_META[tool] || { icon: <I.Bolt size={13} />, label: "Working", running: "Working…" };
  // While the tool runs server-side, show a live progress chip (▸ Researching…)
  // so the multi-agent work feels fast and transparent. It expands into the full
  // result card once the tool_result event lands.
  if (status === "running") {
    return (
      <div className="tool-chip" data-testid={`tool-chip-${tool}`}>
        <span className="tool-chip-caret">▸</span>
        <span className="tool-chip-ico">{meta.icon}</span>
        <span className="tool-chip-label">{meta.running}</span>
        <span className="tool-chip-dots"><i /><i /><i /></span>
      </div>
    );
  }
  return (
    <div className="tool-card" data-testid={`tool-card-${tool}`}>
      <div className="tool-card-header">
        <div className="tool-icon">{meta.icon}</div>
        <div className="tool-name">{meta.label}</div>
        <div className="tool-status done">
          <I.Check size={12} />
        </div>
      </div>
      {status === "done" && children && <div className="tool-result">{children}</div>}
    </div>
  );
}

// ---------- Tool result lists ----------
function ProfList({ profs }) {
  if (!profs?.length) return <div className="muted" style={{ fontSize: 12 }}>No professors found.</div>;
  return (
    <div className="prof-list">
      {profs.slice(0, 5).map((p, i) => (
        <div className="prof-row" key={i}>
          <div className="prof-avatar">{(p.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</div>
          <div>
            <div className="prof-name">{p.name}</div>
            <div className="prof-research">{p.research}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            {p.match != null && <div className="prof-match">{p.match}% match</div>}
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{p.email}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ResearchTable({ data }) {
  if (!data) return null;
  const rows = [
    ["Application deadline", data.deadline],
    ["Tuition", data.tuition],
    ["Acceptance rate", data.acceptRate],
    ["International %", data.intlPct],
    ["GPA", data.gpa_required],
    ["Rank", data.rank],
    ["Application fee", data.application_fee],
    ["Tests", Array.isArray(data.test_requirements) ? data.test_requirements.join(" · ") : null],
  ].filter((r) => r[1]);
  return (
    <table className="extract-table">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}><td>{k}</td><td>{v}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

function ScholList({ items }) {
  if (!items?.length) return <div className="muted" style={{ fontSize: 12 }}>No scholarships found.</div>;
  return (
    <div className="prof-list">
      {items.slice(0, 5).map((s, i) => (
        <div className="prof-row" key={i}>
          <div className="prof-avatar" style={{ background: s.color || "#B85A3D", color: "#FAF8F3" }}>$</div>
          <div>
            <div className="prof-name">{s.name}</div>
            <div className="prof-research">{s.uni} · {s.elig}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="prof-match" style={{ color: "var(--clay-deep)" }}>{s.amount}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{s.deadline}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Message bubbles ----------
function TaskProposal({ proposal, onAddTask }) {
  const [state, setState] = React.useState("idle"); // idle | adding | done
  const add = async () => {
    if (state !== "idle" || !onAddTask) return;
    setState("adding");
    try {
      await onAddTask({
        title: proposal.title,
        uni: proposal.uni || "All applications",
        due: proposal.due || "",
        urgent: !!proposal.urgent,
        src: "AI suggested",
        ai_suggested: true,
      });
      setState("done");
    } catch {
      setState("idle");
    }
  };
  return (
    <div className="task-proposal" data-testid="task-proposal-card">
      <div className="browse-favicon" style={{ background: "var(--clay-deep)", color: "#FAF8F3" }}>
        <I.Check size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="browse-title">{proposal.title}</div>
        <div className="browse-url" style={{ color: "var(--ink-3)" }}>
          {proposal.uni}{proposal.due ? ` · due ${proposal.due}` : ""}
        </div>
      </div>
      {state === "done" ? (
        <span className="pill pill-clay" data-testid="task-proposal-added"><I.Check size={11} /> In Task Center</span>
      ) : (
        <button
          className="btn btn-orange btn-sm task-proposal-btn"
          onClick={add}
          disabled={state === "adding"}
          data-testid="task-proposal-add-btn"
        >
          {state === "adding" ? "Adding…" : "Add to Task Center"}
        </button>
      )}
    </div>
  );
}


export function UserMessage({ text, initials }) {
  return (
    <div className="msg" style={{ alignSelf: "flex-end", flexDirection: "row-reverse" }}>
      <div className="msg-avatar user">{initials}</div>
      <div className="msg-body">
        <div className="msg-role" style={{ textAlign: "right" }}>You</div>
        <div className="msg-text" style={{ background: "var(--bg-sunk)", padding: "10px 14px", borderRadius: "var(--r-md)", display: "inline-block" }}>
          {text}
        </div>
      </div>
    </div>
  );
}

export function AiMessage({ msg, go, streaming, onAddTask }) {
  const hasText = !!(msg.text && msg.text.trim());
  const hasTools = (msg.tools || []).length > 0;
  const isEmptyStreaming = streaming && !hasText && !hasTools;
  return (
    <div className="msg" data-testid={isEmptyStreaming ? "ai-thinking" : undefined}>
      <div className="msg-avatar ai" data-testid="ai-avatar"><VidhyaMark size={16} /></div>
      <div className="msg-body">
        <div className="msg-role">Vidhya Advisor</div>
        <div className="msg-text">
          {(msg.tools || []).map((t, i) => (
            <ToolCard key={i} tool={t.tool} status={t.status}>
              {t.result?.kind === "profs" && <ProfList profs={t.result.data} />}
              {t.result?.kind === "research" && (
                <>
                  <div className="browse-preview" style={{ marginBottom: 12 }}>
                    <div className="browse-favicon" style={{ background: "#B85A3D", color: "#FAF8F3" }}>
                      {t.result.uni?.[0] || "U"}
                    </div>
                    <div>
                      <div className="browse-title">{t.result.uni}</div>
                      <div className="browse-url">{t.result.data?.official_url || "(official page)"}</div>
                    </div>
                  </div>
                  <ResearchTable data={t.result.data} />
                </>
              )}
              {t.result?.kind === "schols" && <ScholList items={t.result.data} />}
              {t.result?.kind === "shortlisted" && (
                <div className="browse-preview" style={{ background: "var(--clay-wash)", borderColor: "transparent" }}>
                  <div className="browse-favicon" style={{ background: t.result.uni.color, color: "#FAF8F3" }}>
                    {t.result.uni.short?.[0] || t.result.uni.name[0]}
                  </div>
                  <div>
                    <div className="browse-title">{t.result.uni.name}</div>
                    <div className="browse-url" style={{ color: "var(--clay-deep)" }}>Added to Researching column</div>
                  </div>
                  <span className="pill pill-clay"><I.Check size={11} /> Added</span>
                </div>
              )}
              {t.result?.kind === "task" && (
                <div className="browse-preview" style={{ background: "var(--clay-wash)", borderColor: "transparent" }}>
                  <div className="browse-favicon" style={{ background: "var(--ink)", color: "#FAF8F3" }}>
                    <I.Check size={20} />
                  </div>
                  <div>
                    <div className="browse-title">{t.result.task.title}</div>
                    <div className="browse-url" style={{ color: "var(--clay-deep)" }}>
                      {t.result.task.uni} · due {t.result.task.due || "no date"}
                    </div>
                  </div>
                  <span className="pill pill-clay"><I.Check size={11} /> Created</span>
                </div>
              )}
              {t.result?.kind === "task_proposal" && (
                <TaskProposal proposal={t.result} onAddTask={onAddTask} />
              )}
              {t.result?.kind === "email" && (
                <div className="email-preview">
                  <div className="email-from">Drafted email</div>
                  <div className="email-subject">{t.result.data?.subject}</div>
                  <div className="email-body" style={{ whiteSpace: "pre-wrap", maxHeight: 220, overflow: "auto" }}>{t.result.data?.body}</div>
                </div>
              )}
              {t.result?.kind === "error" && (
                <div className="auth-error" style={{ marginTop: 0 }}>{t.result.error}</div>
              )}
              {t.result?.kind === "clarify" && (
                <div className="browse-preview" style={{ background: "var(--clay-wash)", borderColor: "transparent" }} data-testid={`tool-clarify-${t.tool}`}>
                  <div className="browse-favicon" style={{ background: "var(--clay-deep)", color: "#FAF8F3" }}>?</div>
                  <div>
                    <div className="browse-title">Couldn't verify that university</div>
                    <div className="browse-url" style={{ color: "var(--clay-deep)" }}>{t.result.message}</div>
                  </div>
                </div>
              )}
              {t.result?.kind === "upgrade" && (
                <div className="tool-upgrade-card" data-testid={`tool-upgrade-${t.tool}`}>
                  <div className="tool-upgrade-title">{t.result.title}</div>
                  <div className="tool-upgrade-copy">{t.result.copy}</div>
                  <UpgradeButton
                    feature={t.result.feature}
                    context={t.result.context}
                    label="Unlock with Navigator"
                    size="sm"
                    testid={`tool-upgrade-btn-${t.tool}`}
                  />
                </div>
              )}
            </ToolCard>
          ))}
          {renderRichText(msg.text)}
          {streaming && hasText && <span className="cursor"></span>}
          {isEmptyStreaming && (
            <StepReveal
              steps={chatStepsForMessage(msg.intentText)}
              active
              testid="chat-steps"
            />
          )}
        </div>
      </div>
    </div>
  );
}
